import { Stack } from "@mantine/core";
import type { FieldError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSafeMissingData } from "../../../../hooks/useMissingData";
import { extractErrorString } from "../../../../utils/extractErrorString";
import { camelize } from "../../../../utils/SnakeCamelConversion";
import {
  FormFieldAsyncSelect,
  type FormFieldAsyncSelectProps
} from "./fields/FormFieldAsyncSelect";
import {
  FormFieldBoolean,
  type FormFieldBooleanProps
} from "./fields/FormFieldBoolean";
import {
  FormFieldDateInput,
  type FormFieldDateInputProps
} from "./fields/FormFieldDateInput";
import {
  FormFieldDateRangeInput,
  type FormFieldDateRangeInputProps
} from "./fields/FormFieldDateRangeInput";
import {
  FormFieldDropzone,
  type FormFieldDropzoneProps
} from "./fields/FormFieldDropzone";
import {
  FormFieldExpression,
  type FormFieldExpressionProps
} from "./fields/FormFieldExpression";
import {
  FormFieldMultipleEmails,
  type FormFieldMultipleEmailsProps
} from "./fields/FormFieldMultipleEmails";
import {
  FormFieldMultiSelect,
  type FormFieldMultiSelectProps
} from "./fields/FormFieldMultiSelect";
import {
  FormFieldNumberInput,
  type FormFieldNumberInputProps
} from "./fields/FormFieldNumberInput";
import {
  FormFieldPasswordInput,
  type FormFieldPasswordInputProps
} from "./fields/FormFieldPasswordInput";
import {
  FormFieldRadioGroup,
  type FormFieldRadioGroupProps
} from "./fields/FormFieldRadioGroup";
import {
  FormFieldSelect,
  type FormFieldSelectProps
} from "./fields/FormFieldSelect";
import {
  FormFieldSelectWithCreate,
  type FormFieldSelectWithCreateProps
} from "./fields/FormFieldSelectWithCreate";
import {
  FormFieldSwitch,
  type FormFieldSwitchProps
} from "./fields/FormFieldSwitch";
import {
  FormFieldTextarea,
  type FormFieldTextareaProps
} from "./fields/FormFieldTextarea";
import {
  FormFieldTextInput,
  type FormFieldTextInputProps
} from "./fields/FormFieldTextInput";
import {
  FormFieldUploadableImage,
  type FormFieldUploadableImageProps
} from "./fields/FormFieldUploadableImage";
import { FormFieldInfoText } from "./FormFieldInfoText/FormFieldInfoText";
import { HelperText } from "./HelperText/HelperText";

/** Properties which can be passed to form fields of type "custom". */
type CommonFormFieldProps = {
  error?: string | FieldError;
  helpText?: string;
  hideFieldError?: boolean;
  highlight?: boolean;
  infoText?: string;
  isBetaFeature?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  warned?: boolean;
  warning?: string;
};

/** Subset of properties of CommonFormFieldProps which will be passed to the component in the Controller `render` function. */
type CommonFormFieldItemProps = Pick<
  CommonFormFieldProps,
  "helpText" | "highlight" | "isBetaFeature" | "warned"
>;

/** Properties which can be passed to form fields, includes all types. */
type FormFieldProps = CommonFormFieldProps &
  (
    | Omit<FormFieldTextInputProps, "error">
    | Omit<FormFieldPasswordInputProps, "error">
    | Omit<FormFieldNumberInputProps, "error">
    | Omit<FormFieldTextareaProps, "error">
    | Omit<FormFieldRadioGroupProps, "error">
    | Omit<FormFieldBooleanProps, "error">
    | Omit<FormFieldDateInputProps, "error">
    | Omit<FormFieldDateRangeInputProps, "error">
    | Omit<FormFieldSelectProps, "error">
    | Omit<FormFieldSelectWithCreateProps, "error">
    | Omit<FormFieldAsyncSelectProps, "error">
    | Omit<FormFieldMultiSelectProps, "error">
    | Omit<FormFieldMultipleEmailsProps, "error">
    | Omit<FormFieldUploadableImageProps, "error">
    | Omit<FormFieldDropzoneProps, "error">
    | Omit<FormFieldExpressionProps, "error" | "onChange">
    | Omit<FormFieldSwitchProps, "error">
  );

function FormField({
  error,
  helpText,
  hideFieldError,
  highlight,
  infoText,
  isBetaFeature,
  type,
  warned,
  warning,
  ...fieldSpecificProps
}: FormFieldProps) {
  const { t } = useTranslation();

  const { missingData } = useSafeMissingData();
  const missingFields = Object.values(missingData?.missingFields ?? {}).flat();
  const isHighlighted =
    highlight ||
    (!!fieldSpecificProps.name &&
      missingFields.some((field) => {
        const camelCaseName = fieldSpecificProps.name
          ? camelize(fieldSpecificProps.name)
          : "";
        return camelCaseName.includes(field);
      }));

  const hasError = !!error && !hideFieldError;
  const extractedError = extractErrorString(t, error);

  const sharedFieldProps = {
    error: hasError ? extractedError : undefined,
    helpText: helpText,
    highlight: isHighlighted,
    isBetaFeature: isBetaFeature,
    warned: warned
  };

  return (
    <Stack className="FormField" gap="xs" mb="sm">
      {(type === "text" || type === "email") && (
        <FormFieldTextInput
          {...(fieldSpecificProps as FormFieldTextInputProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "password" && (
        <FormFieldPasswordInput
          {...(fieldSpecificProps as FormFieldPasswordInputProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "number" && (
        <FormFieldNumberInput
          {...(fieldSpecificProps as FormFieldNumberInputProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "textarea" && (
        <FormFieldTextarea
          {...(fieldSpecificProps as FormFieldTextareaProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "radio" && (
        <FormFieldRadioGroup
          {...(fieldSpecificProps as FormFieldRadioGroupProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "boolean" && (
        <FormFieldBoolean
          {...(fieldSpecificProps as FormFieldBooleanProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "date" && (
        <FormFieldDateInput
          {...(fieldSpecificProps as FormFieldDateInputProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "date-range" && (
        <FormFieldDateRangeInput
          {...(fieldSpecificProps as FormFieldDateRangeInputProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "select" && (
        <FormFieldSelect
          {...(fieldSpecificProps as FormFieldSelectProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "select-with-create" && (
        <FormFieldSelectWithCreate
          {...(fieldSpecificProps as FormFieldSelectWithCreateProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "async-select" && (
        <FormFieldAsyncSelect
          {...(fieldSpecificProps as FormFieldAsyncSelectProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "multiselect" && (
        <FormFieldMultiSelect
          {...(fieldSpecificProps as FormFieldMultiSelectProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "multiple-emails" && (
        <FormFieldMultipleEmails
          {...(fieldSpecificProps as FormFieldMultipleEmailsProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "uploadable-image" && (
        <FormFieldUploadableImage
          {...(fieldSpecificProps as FormFieldUploadableImageProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "dropzone" && (
        <FormFieldDropzone
          {...(fieldSpecificProps as FormFieldDropzoneProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "expression" && (
        <FormFieldExpression
          {...(fieldSpecificProps as FormFieldExpressionProps)}
          {...sharedFieldProps}
        />
      )}
      {type === "switch" && (
        <FormFieldSwitch
          {...(fieldSpecificProps as FormFieldSwitchProps)}
          {...sharedFieldProps}
        />
      )}
      {warning && <HelperText warning={warning} />}
      {infoText && <FormFieldInfoText infoText={infoText} />}
    </Stack>
  );
}

export {
  CommonFormFieldItemProps,
  CommonFormFieldProps,
  FormField,
  FormFieldProps
};
